<template>
  <section class="service-log">
    <Loading v-if="localLoading || isLoading" />
    <template v-else>
      <section class="info-panel" v-if="isShowBarItems">
        <BarItem
          v-for="(item, index) in serviceLogBarItems"
          :isAmended="item.isAmended"
          :trendInfo="item.trendInfo"
          :key="item.label"
          componentType="info"
          :text="item.text"
          :subText="item.subText"
          :hint="item.hint"
          :label="$t(item.label)"
          :iconUrl="item.iconUrl"
          :spaceIconText="true"
          :class="{
            last: index === serviceLogBarItems.length - 1,
            first: index === 0,
          }"
        />
      </section>
      <template v-if="currOpenTab.isRoomAnalyticsLog">
        <AnalyticsChartTable
          @chartClick="handleChartClick"
          v-if="data"
          :data="chartData"
          class="chart-table"
        />
        <TableByHour
          v-if="data.hourData"
          :dataByHour="dataForHourTable"
          class="table-by-hour"
        />
      </template>
      <template v-if="currOpenTab.isCleanLog || currOpenTab.isMaintenanceLog">
        <NoData v-if="tableData.length === 0" />
        <IssueTimesTable v-else :tableData="tableData" />
      </template>
      <CleanerArrival v-if="currOpenTab.isCleanerArrival && cleanerArrivals" :cleanerArrivals="cleanerArrivals" />
      <TimelineExposures v-if="currOpenTab.isRoomExposures && roomExposures" :roomExposures="roomExposures" />
    </template>
    <IframeCMP
      v-if="isPrinting && currOpenTab.isRoomAnalyticsLog && canPrintRoomAnalytics"
      :info-bar-items="infoBarItemsForPdf"
      :analytics-data="chartDataForPdf"
      :hour-data="hourDataForPdf"
      :hide-info="hideInfo"
      :rooms-data="roomsDataForPdf"
      pdf-title-i18n-key="analytics_log"
      :include-room-line-in-pdf="true"
      :pdf-filename-suffix="roomPdfFilenameSuffix"
      @donePrinting="$emit('donePrinting')"
      class="pdf-print-iframe"
    />
  </section>
</template>

<script>
import AnalyticsChartTable from "../components/AnalyticsChartTable";
import CleanerArrival from './CleanerArrival';
import TimelineExposures from "./TimelineExposures";
import TableByHour from './TableByHour';
import BarItem from "../components/BarItem";
import IssueTimesTable from './IssueTimesTable';
import Loading from "@/modules/common/components/Loading";
import NoData from "@/modules/common/components/NoData";
import { infoBarItems, trendArrowColors } from "../consts/consts";
import { getOverviewSums, getAnalyticsSums, getRoomsCounterCallsSum, getRoomsCleandexCallsSum, getRoomsManualCallsSum, getRoomsUnnecessaryArrivals, isMetricShown, getUnnecessaryArrivalsShareText } from "../services/performanceService";
import { hasServikNameFilter, resolveServikIdFromLabel } from "@/modules/common/services/filterService";
import utilService from "@/modules/common/services/utilService";
import { mapGetters } from 'vuex';
import IframeCMP from './IframeCMP';

export default {
  name: 'ServiceLog',
  props: {
    cleanerArrivals: { type: Array, required: false },
    roomExposures: { type: Array, required: false },
    tableData: { type: Array, required: false, default: () => [] },
    isPrinting: { type: Boolean, default: false },
    hideInfo: { type: Array, default: () => [] },
  },
  data() {
    return {
      data: null,
      localLoading: false,
      serviceLogBarItems: []
    };
  },
  mounted() {
    // Initialize data from store
    this.data = this.$store.getters.analyticsData;
    
    // Initialize bar items only when data is ready
    if (this.data && this.overviewData) {
      this.serviceLogBarItems = this.serviceLogBarItemsByRoute;
      this.updateBarItems();
    } else {
      this.serviceLogBarItems = [];
    }
  },
  methods: {
    async handleChartClick(servikFilter) {
      // Set loading state to prevent showing stale data
      this.$store.commit({ type: 'toggleLoading', value: true })
      
      // Update both filters in the same tick to ensure both are included in the API call
      // Use nextTick to batch the updates so watchers fire after both are committed
      const servikId = resolveServikIdFromLabel(
        servikFilter.label,
        this.$store.getters.filterOptions?.servikName || [],
      )
      if (servikId == null) {
        this.$store.commit({ type: 'toggleLoading', value: false })
        return
      }
      this.$store.commit({ type: 'updateFilter', field: 'servikName', value: [servikId] })
      this.$store.commit({ type: 'updateFilter', field: 'rooms', value: [this.roomId] })
      
      // Use nextTick to ensure both filter updates are processed before watchers fire
      await this.$nextTick()
    },
    updatePageData() {
      // Update data from store
      this.data = this.$store.getters.analyticsData;
      
      // Update bar items if data is ready
      if (this.data && this.overviewData) {
        this.updateBarItems();
      }
      
      if (!this.data) this.$router.push({ name: "analytics" });
    },
    updateBarItems() {
      if (!this.data) return
      // Don't update bar items if overviewData is not ready yet
      // This prevents showing "N/A" or incorrect values
      if (!this.overviewData) return
      
      const { data } = this
      if (!data.hourData || !data.roomsData) return
      
      const overviewSums = getOverviewSums(this.overviewData);
      const analyticsSums = getAnalyticsSums(data.hourData);
      analyticsSums.counterCalls = getRoomsCounterCallsSum(data.roomsData)
      analyticsSums.cleandexCalls = getRoomsCleandexCallsSum(data.roomsData)
      analyticsSums.manualCalls = getRoomsManualCallsSum(data.roomsData)
      // Bar visits: use same source as room table (sum of room.visits from shift_room_stats)
      if (data.roomsData && data.roomsData.length) {
        const totalVisits = data.roomsData.reduce((sum, r) => sum + (Number(r.visits) || 0), 0)
        analyticsSums.visits = this.roomId
          ? (Number(data.roomsData.find((r) => String(r._id) === String(this.roomId))?.visits) || 0)
          : totalVisits
      }
      if (data.roomsData && data.roomsData.some((room) => room.isArrivalsAmended)) {
        analyticsSums.isArrivalsAmended = 1
      }

      const roomRows = this.roomId
        ? data.roomsData.filter((room) => String(room._id) === String(this.roomId))
        : data.roomsData
      const unnecessaryArrivals = getRoomsUnnecessaryArrivals(roomRows)

      // Initialize bar items structure if empty (prevents showing "N/A" before data is ready)
      if (this.serviceLogBarItems.length === 0) {
        this.serviceLogBarItems = this.serviceLogBarItemsByRoute;
      }

      // Ensure overviewSums and analyticsSums are valid objects
      if (!overviewSums || !analyticsSums) return

      const trendInfo = this.$store.getters.trendInfo || {};

      this.serviceLogBarItems = this.serviceLogBarItems.reduce((acc, item) => {
        const { name } = item;
        const { kFormatter } = utilService

        const isShown = isMetricShown(
          name,
          analyticsSums[name] || overviewSums[name],
          unnecessaryArrivals,
          this.filterSelected?.servikName
        )
        if (isShown) {
          item.trendInfo = trendInfo && trendInfo[item.name] ? trendInfo[item.name] : null;
          if (item.trendInfo && trendArrowColors[item.name]) {
            item.trendInfo.color = trendArrowColors[item.name][item.trendInfo.upDown];
          }
          const itemsIncludesTime = ['response_time_SLA']
          const isTypeTime = itemsIncludesTime.includes(name)
          if (name === "visits") item.isAmended = analyticsSums.isAmended || overviewSums.isAmended
          if (name === "arrivals") item.isAmended = analyticsSums.isArrivalsAmended
          if (name === 'unnecessary_arrivals') {
            item.text = kFormatter(unnecessaryArrivals.count)
            item.subText = getUnnecessaryArrivalsShareText(unnecessaryArrivals, this.$t('of_arrivals'))
            item.hint = this.$t('unnecessary_arrivals_hint')
          } else if (name === 'avg_score' || name === 'response_time_SLA') {
            item.text = kFormatter(overviewSums[name] || 0)
          } else if (name === 'complaints') {
            item.text = kFormatter(analyticsSums[name])
          } else if (name === 'exposures') {
            item.text = this.addPercentageSign(overviewSums[name] || 0)
          } else {
            item.text = kFormatter(analyticsSums[name] || overviewSums[name] || 0)
          }
          if (isTypeTime) {
            item.text = this.addMinutesText(item.text)
          }
          acc.push(item);
        }
        return acc;
      }, []);
    },
    getTotalCleaningComplaints() {
      if (!this.data || !this.data.charts || !this.data.charts.cleaning) return 0
      const { charts: { cleaning : { data: cleaningComplaints = [] } } } = this.data
      const totalCleaningComplaints = cleaningComplaints.reduce((acc, item) => acc + item, 0)
      return totalCleaningComplaints;
    },
    addPercentageSign(value) {
      return value + '%';
    },
    addMinutesText(value) {
      return `${value} ${this.$t("min")}`;
    }
  },
  watch: {
    // Watch route changes to update bar items when switching tabs
    '$route.name': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.data = this.$store.getters.analyticsData;
        this.serviceLogBarItems = this.serviceLogBarItemsByRoute;
        if (this.data && this.overviewData) {
          this.updateBarItems();
        }
      }
    },
    // Watch overviewData to update bar items when it becomes available
    overviewData: {
      handler(newValue) {
        if (newValue && this.data) {
          this.updateBarItems();
        }
      },
      immediate: false
    }
  },
  computed: {
    ...mapGetters(['filterSelected', 'overviewData', 'isLoading', 'filterOptions']),
    resolvedAnalyticsData() {
      return this.$store.getters.analyticsData || this.data;
    },
    canPrintRoomAnalytics() {
      const d = this.resolvedAnalyticsData;
      return !!(d && d.charts);
    },
    chartDataForPdf() {
      const d = this.resolvedAnalyticsData;
      if (!d || !d.charts) return {};
      const { cleaning, totalLikes, maintenance } = d.charts;
      return { cleaning, totalLikes, maintenance };
    },
    hourDataForPdf() {
      const d = this.resolvedAnalyticsData;
      if (!d || !d.hourData) return [];
      return d.hourData;
    },
    roomsDataForPdf() {
      const d = this.resolvedAnalyticsData;
      if (!d || !d.roomsData) return [];
      const id = this.roomId;
      const rows = d.roomsData.filter((r) => String(r._id) === String(id));
      return rows;
    },
    roomPdfFilenameSuffix() {
      const opts = this.filterOptions;
      const rid = this.roomId;
      if (!opts || !Array.isArray(opts.rooms)) return `room-${rid}`;
      const room = opts.rooms.find((r) => String(r.id) === String(rid));
      if (!room) return `room-${rid}`;
      const n = String(room.name || '').replace(/[/\\:*?"<>|]/g, '-').trim();
      return n || `room-${rid}`;
    },
    infoBarItemsForPdf() {
      let items = this.serviceLogBarItems || [];
      if (this.hideInfo && this.hideInfo.length) {
        items = items.filter((col) => this.hideInfo.indexOf(col.name) === -1);
      }
      return items;
    },
    serviceLogBarItemsByRoute() {
      const { currOpenTab } = this
      if (!currOpenTab.isCleanLog && !currOpenTab.isMaintenanceLog) {
        return JSON.parse(JSON.stringify(infoBarItems))
      }
      return []
    },
    dataForHourTable() {
      if (!this.data) return []
      const { data: { hourData }, currOpenTab } = this
      if (hasServikNameFilter(this.$store.getters.filterSelected.servikName) || currOpenTab.isRoomAnalyticsLog) {
        return hourData
      }
      return []
    },
    chartData() {
      if (!this.data || !this.data.charts) return {}
      const { data: { charts: { cleaning, totalLikes, maintenance } }, currOpenTab } = this
      if (currOpenTab.isRoomAnalyticsLog) {
        return { cleaning, totalLikes, maintenance }
      }
      return {}
    },
    roomId() {
      return this.$route.params.roomId;
    },
    isShowBarItems() {
      const { routeName } = this
      const notShowBarItemsIn = ['cleanLog', 'maintenanceLog']
      return !notShowBarItemsIn.includes(routeName)
    },
    currOpenTab() {
      const { routeName } = this
      const routeNameCapitalized = routeName.charAt(0).toUpperCase() + routeName.slice(1)
      const key = `is${routeNameCapitalized}`
      return { [key]: true }
    },
    routeName() {
      return this.$route.name;
    }
  },
  components: {
    BarItem,
    AnalyticsChartTable,
    Loading,
    IssueTimesTable,
    TableByHour,
    CleanerArrival,
    TimelineExposures,
    NoData,
    IframeCMP,
  }
};
</script>

<style lang="scss" scoped>
.info-panel {
  // Grid (not flex) guarantees every card gets the exact same column width,
  // regardless of how much text/content any single card holds.
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(150px, 1fr);
  align-items: stretch;
  gap: 12px;
  margin-bottom: 30px;
  overflow-x: auto;
  padding-bottom: 4px;
  padding-top: 4px;
  scrollbar-width: thin;

  > * {
    min-width: 0;
  }
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
}

.chart-table {
  margin-bottom: 80px;
}

.table-by-hour {
  margin-bottom: 35px;
}

.pdf-print-iframe {
  position: fixed;
  left: -12000px;
  top: 0;
  width: 1400px;
  min-width: 1400px;
  max-height: none;
  overflow: visible;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
}
</style>